import { useEffect } from "react";
import { useDebugStore } from "@/store/useDebugStore";

export const DebugMenu = () => {
  const {
    debugMenuOpen,
    toggleDebugMenu,
    setDebugMenuOpen,
    lessonStartupPerfOverride,
    setLessonStartupPerfOverride,
    lessonCompleteOverride,
    setLessonCompleteOverride,
  } = useDebugStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        toggleDebugMenu();
      }
      if (e.key === "Escape" && debugMenuOpen) {
        setDebugMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [debugMenuOpen, toggleDebugMenu, setDebugMenuOpen]);

  if (!debugMenuOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 min-w-[320px] max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Debug Menu</h2>
          <button
            onClick={() => setDebugMenuOpen(false)}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div className="border-b pb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lesson Startup A/B
            </label>
            <div className="flex gap-2">
              {(["default", "optimized", "baseline"] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setLessonStartupPerfOverride(option)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    lessonStartupPerfOverride === option
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Baseline disables slide HTML cache and idle prefetch for local
              startup comparison.
            </p>
          </div>

          <div className="border-b pb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lesson Complete Variant
            </label>
            <div className="flex gap-2">
              {(["default", "celebrate", "quiet"] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setLessonCompleteOverride(option)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    lessonCompleteOverride === option
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Forces the end-of-lesson modal variant. Does not override a manual
              exit — Exit Lesson and Save &amp; Exit still show no modal at all.
            </p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t text-xs text-gray-400">
          Press{" "}
          <kbd className="px-1 py-0.5 bg-gray-100 rounded">Ctrl+Shift+D</kbd> to
          toggle this menu
        </div>
      </div>
    </div>
  );
};
